import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Typography,
  Form,
  Space,
  message,
  Tabs,
  Row,
  Col,
  Tag,
  Tooltip,
  Timeline,
  Select,
  Input,
  Popconfirm,
  Calendar,
  Steps,
  Checkbox,
  Modal,
  Empty,
  Progress,
  Segmented,
  Table,
  DatePicker
} from 'antd';
import {
  SyncOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  FireOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  StarFilled,
  StarOutlined,
  TrophyOutlined,
  RiseOutlined,
  AimOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { habitsConfig } from '../config/habits';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import dayjs from 'dayjs';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;

const BRAND = '#6366F1';

const Habits = () => {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [habits, setHabits] = useState([]);
  const [goodHabits, setGoodHabits] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHabitModal, setShowHabitModal] = useState(false);
  const [showGoodHabitModal, setShowGoodHabitModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('habits');
  const [viewMode, setViewMode] = useState('card');
  const [selectedHabit, setSelectedHabit] = useState(null);
  const [selectedGoodHabit, setSelectedGoodHabit] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [checkinData, setCheckinData] = useState({});
  // 工具栏筛选
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [onlyStarred, setOnlyStarred] = useState(false);
  const [sortKey, setSortKey] = useState('recent');
  const [habitForm] = Form.useForm();
  const [goodHabitForm] = Form.useForm();
  const [progressForm] = Form.useForm();
  const [checkinForm] = Form.useForm();

  // 加载坏习惯数据
  const fetchHabits = async () => {
    setLoading(true);
    try {
      const response = await axios.get(habitsConfig.apiEndpoints.habits);
      if (response.data.success) {
        setHabits(response.data.habits || []);
      } else {
        message.error(response.data.message || '获取习惯数据失败');
      }
    } catch (error) {
      console.error('获取习惯数据失败:', error);
      message.error('获取习惯数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载打卡数据
  const fetchCheckins = async () => {
    try {
      const response = await axios.get(habitsConfig.apiEndpoints.checkins);
      if (response.data.success) {
        setCheckins(response.data.checkins || []);
        // 将打卡数据转换为以日期为key的对象
        const checkinMap = {};
        response.data.checkins.forEach(checkin => {
          checkinMap[checkin.checkin_date] = checkin;
        });
        setCheckinData(checkinMap);
      } else {
        message.error(response.data.message || '获取打卡数据失败');
      }
    } catch (error) {
      console.error('获取打卡数据失败:', error);
      message.error('获取打卡数据失败');
    }
  };

  // 加载好习惯数据
  const fetchGoodHabits = async () => {
    setLoading(true);
    try {
      const response = await axios.get(habitsConfig.apiEndpoints.goodHabits);
      if (response.data.success) {
        setGoodHabits(response.data.goodHabits || []);
      } else {
        message.error(response.data.message || '获取好习惯数据失败');
      }
    } catch (error) {
      console.error('获取好习惯数据失败:', error);
      message.error('获取好习惯数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载习惯打卡统计总览
  const fetchOverview = async () => {
    try {
      const response = await axios.get(habitsConfig.apiEndpoints.stats + '/overview');
      if (response.data.success) {
        setOverview(response.data.overview);
      }
    } catch (error) {
      console.error('获取习惯总览失败:', error);
    }
  };

  // 打开坏习惯弹窗
  const handleOpenHabit = (habit = null) => {
    setSelectedHabit(habit);
    if (habit) {
      // 编辑现有习惯
      habitForm.setFieldsValue({
        name: habit.name,
        negative_impact: habit.negative_impact,
        difficulty: habit.difficulty,
        status: habit.status,
        notes: habit.notes,
        is_starred: !!habit.is_starred,
        start_date: habit.start_date ? dayjs(habit.start_date) : null
      });
    } else {
      // 新建习惯
      habitForm.resetFields();
    }
    setShowHabitModal(true);
  };

  // 保存坏习惯
  const handleSaveHabit = async (values) => {
    try {
      const requestBody = {
        user_id: 1, // 临时使用固定用户ID，实际应该从token解析
        name: values.name,
        negative_impact: values.negative_impact,
        difficulty: values.difficulty,
        status: values.status,
        notes: values.notes,
        is_starred: values.is_starred || false,
        start_date: values.start_date ? (dayjs.isDayjs(values.start_date) ? values.start_date.format('YYYY-MM-DD') : values.start_date) : null
      };

      let response;
      if (selectedHabit) {
        response = await axios.put(`${habitsConfig.apiEndpoints.update}/${selectedHabit.id}`, requestBody);
      } else {
        response = await axios.post(habitsConfig.apiEndpoints.create, requestBody);
      }

      if (response.data.success) {
        message.success(selectedHabit ? '习惯更新成功' : '习惯记录成功');
        setShowHabitModal(false);
        fetchHabits();
        fetchOverview();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存习惯失败:', error);
      message.error('操作失败');
    }
  };

  // 删除坏习惯
  const handleDeleteHabit = async (habitId) => {
    try {
      const response = await axios.delete(`${habitsConfig.apiEndpoints.delete}/${habitId}`);
      if (response.data.success) {
        message.success('习惯删除成功');
        fetchHabits();
        fetchOverview();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除习惯失败:', error);
      message.error('删除失败');
    }
  };

  // 打开好习惯弹窗
  const handleOpenGoodHabit = (habit = null) => {
    setSelectedGoodHabit(habit);
    if (habit) {
      // 编辑现有好习惯
      goodHabitForm.setFieldsValue({
        name: habit.name,
        positive_impact: habit.positive_impact,
        difficulty: habit.difficulty,
        status: habit.status,
        notes: habit.notes,
        is_starred: !!habit.is_starred,
        start_date: habit.start_date ? dayjs(habit.start_date) : null
      });
    } else {
      // 新建好习惯
      goodHabitForm.resetFields();
    }
    setShowGoodHabitModal(true);
  };

  // 保存好习惯
  const handleSaveGoodHabit = async (values) => {
    try {
      const requestBody = {
        user_id: 1, // 临时使用固定用户ID，实际应该从token解析
        name: values.name,
        positive_impact: values.positive_impact,
        difficulty: values.difficulty,
        status: values.status,
        notes: values.notes,
        is_starred: values.is_starred || false,
        start_date: values.start_date ? (dayjs.isDayjs(values.start_date) ? values.start_date.format('YYYY-MM-DD') : values.start_date) : null
      };

      let response;
      if (selectedGoodHabit) {
        response = await axios.put(`${habitsConfig.apiEndpoints.updateGoodHabit}/${selectedGoodHabit.id}`, requestBody);
      } else {
        response = await axios.post(habitsConfig.apiEndpoints.createGoodHabit, requestBody);
      }

      if (response.data.success) {
        message.success(selectedGoodHabit ? '好习惯更新成功' : '好习惯创建成功');
        setShowGoodHabitModal(false);
        fetchGoodHabits();
        fetchOverview();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存好习惯失败:', error);
      message.error('操作失败');
    }
  };

  // 删除好习惯
  const handleDeleteGoodHabit = async (habitId) => {
    try {
      const response = await axios.delete(`${habitsConfig.apiEndpoints.deleteGoodHabit}/${habitId}`);
      if (response.data.success) {
        message.success('好习惯删除成功');
        fetchGoodHabits();
        fetchOverview();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除好习惯失败:', error);
      message.error('删除失败');
    }
  };

  // 打开进度弹窗
  const handleOpenProgress = (habit) => {
    setSelectedHabit(habit);
    progressForm.resetFields();
    setShowProgressModal(true);
  };

  // 保存进度
  const handleSaveProgress = async (values) => {
    try {
      const requestBody = {
        habit_id: selectedHabit.id,
        content: values.content,
        progress: values.progress,
        notes: values.notes
      };

      const response = await axios.post(habitsConfig.apiEndpoints.addProgress, requestBody);

      if (response.data.success) {
        message.success('进度记录成功');
        setShowProgressModal(false);
        fetchHabits();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存进度失败:', error);
      message.error('操作失败');
    }
  };

  // 打开打卡弹窗
  const handleOpenCheckin = (date) => {
    setSelectedDate(date);
    const dateStr = date.format('YYYY-MM-DD');
    const existingCheckin = checkinData[dateStr];

    if (existingCheckin) {
      // 编辑现有打卡
      checkinForm.setFieldsValue({
        water_intake: existingCheckin.water_intake === 1,
        exercise: existingCheckin.exercise === 1,
        reading: existingCheckin.reading === 1,
        early_rise: existingCheckin.early_rise === 1,
        early_sleep: existingCheckin.early_sleep === 1,
        social_contact: existingCheckin.social_contact === 1,
        note: existingCheckin.note,
        mood: existingCheckin.mood
      });
    } else {
      // 新建打卡
      checkinForm.resetFields();
    }
    setShowCheckinModal(true);
  };

  // 保存打卡
  const handleSaveCheckin = async (values) => {
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const existingCheckin = checkinData[dateStr];

      const requestBody = {
        checkin_date: dateStr,
        water_intake: values.water_intake || false,
        exercise: values.exercise || false,
        reading: values.reading || false,
        early_rise: values.early_rise || false,
        early_sleep: values.early_sleep || false,
        social_contact: values.social_contact || false,
        note: values.note || null,
        mood: values.mood || null
      };

      let response;
      if (existingCheckin) {
        response = await axios.put(`${habitsConfig.apiEndpoints.updateCheckin}/${existingCheckin.id}`, requestBody);
      } else {
        response = await axios.post(habitsConfig.apiEndpoints.createCheckin, requestBody);
      }

      if (response.data.success) {
        message.success(existingCheckin ? '打卡更新成功' : '打卡成功');
        setShowCheckinModal(false);
        fetchCheckins();
        fetchOverview();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存打卡失败:', error);
      console.error('错误详情:', error.response?.data);
      message.error('操作失败');
    }
  };

  // 计算打卡完成率
  const calculateCompletionRate = (checkin) => {
    if (!checkin) return 0;

    const fields = ['water_intake', 'exercise', 'reading', 'early_rise', 'early_sleep', 'social_contact'];
    let completedItems = 0;

    fields.forEach(field => {
      if (checkin[field]) {
        completedItems++;
      }
    });

    return Math.round((completedItems / fields.length) * 100);
  };

  // 获取打卡状态
  const getCheckinStatus = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    const checkin = checkinData[dateStr];

    if (!checkin) return 'none';

    const completionRate = calculateCompletionRate(checkin);
    if (completionRate >= 80) return 'excellent';
    if (completionRate >= 60) return 'good';
    if (completionRate >= 40) return 'fair';
    return 'poor';
  };

  // 获取打卡状态颜色
  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return '#52C41A';
      case 'good': return '#1890FF';
      case 'fair': return '#FAAD14';
      case 'poor': return '#FF4D4F';
      default: return '#D9D9D9';
    }
  };

  // 月历单元格渲染
  const dateCellRender = (date) => {
    const status = getCheckinStatus(date);

    if (status === 'none') return null;

    return (
      <div style={{
        position: 'absolute',
        top: '2px',
        right: '2px',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: getStatusColor(status)
      }} />
    );
  };

  // 获取习惯状态颜色
  const getHabitStatusColor = (status) => {
    const statusItem = habitsConfig.habitStatus.find(s => s.key === status);
    return statusItem ? statusItem.color : '#D9D9D9';
  };

  // 获取纠正难度颜色
  const getDifficultyColor = (difficulty) => {
    const difficultyItem = habitsConfig.difficultyLevels.find(d => d.key === difficulty);
    return difficultyItem ? difficultyItem.color : '#D9D9D9';
  };

  const statusName = (key) => habitsConfig.habitStatus.find(s => s.key === key)?.name || key || '-';
  const difficultyName = (key) => habitsConfig.difficultyLevels.find(d => d.key === key)?.name || key || '-';

  // 组件加载时获取数据
  useEffect(() => {
    initialize();
    fetchHabits();
    fetchGoodHabits();
    fetchCheckins();
    fetchOverview();
  }, [initialize]);

  // 客户端筛选/排序（工具栏）
  const applyFilters = (list, impactKey) => {
    const kw = searchText.trim();
    let out = (list || []).filter((h) => {
      if (kw) {
        const hay = `${h.name || ''} ${h[impactKey] || ''} ${h.notes || ''}`.toLowerCase();
        if (!hay.includes(kw.toLowerCase())) return false;
      }
      if (filterStatus !== 'all' && h.status !== filterStatus) return false;
      if (filterDifficulty !== 'all' && h.difficulty !== filterDifficulty) return false;
      if (onlyStarred && !h.is_starred) return false;
      return true;
    });
    out = [...out].sort((a, b) => {
      if (sortKey === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortKey === 'starred') return (b.is_starred ? 1 : 0) - (a.is_starred ? 1 : 0);
      return (b.id || 0) - (a.id || 0); // recent
    });
    return out;
  };

  const filteredHabits = applyFilters(habits, 'negative_impact');
  const filteredGoodHabits = applyFilters(goodHabits, 'positive_impact');

  // 统计卡数据
  const ov = overview || {};
  const today = ov.today || {};
  const habitCount = ov.habits?.total ?? habits.length;
  const goodCount = ov.goodHabits?.total ?? goodHabits.length;
  const formedCount = (ov.habits?.completed || 0) + (ov.goodHabits?.completed || 0);

  const statCards = [
    {
      icon: <AimOutlined />, color: BRAND,
      value: today.checkedIn ? `${today.done}/${today.total ?? 6}` : `0/${today.total ?? 6}`,
      label: today.checkedIn ? '今日已打卡项' : '今日待打卡',
      danger: !today.checkedIn,
      onClick: () => handleOpenCheckin(dayjs())
    },
    {
      icon: <FireOutlined />, color: '#FA541C',
      value: `${ov.streak ?? 0}`, label: '连续打卡(天)',
      onClick: () => setActiveTab('calendar')
    },
    {
      icon: <RiseOutlined />, color: '#52C41A',
      value: `${ov.avgCompletion ?? 0}%`, label: '近30天完成率'
    },
    {
      icon: <CalendarOutlined />, color: '#1890FF',
      value: `${ov.monthlyCheckins ?? 0}`, label: '本月打卡(次)'
    },
    {
      icon: <ExclamationCircleOutlined />, color: '#FAAD14',
      value: `${habitCount}`, label: '坏习惯',
      onClick: () => setActiveTab('habits')
    },
    {
      icon: <TrophyOutlined />, color: '#722ED1',
      value: `${goodCount}`, label: '好习惯',
      onClick: () => setActiveTab('good-habits')
    }
  ];

  // 表格列（复用于坏/好习惯）
  const buildColumns = (isGood) => ([
    {
      title: '习惯', dataIndex: 'name', key: 'name',
      render: (name, r) => (
        <Space>
          {r.is_starred && <StarFilled style={{ color: '#FAAD14' }} />}
          <Text strong>{name}</Text>
        </Space>
      )
    },
    {
      title: isGood ? '积极影响' : '负面影响',
      dataIndex: isGood ? 'positive_impact' : 'negative_impact',
      key: 'impact',
      ellipsis: true,
      render: (v) => <Text type="secondary">{v || '-'}</Text>
    },
    {
      title: '难度', dataIndex: 'difficulty', key: 'difficulty', width: 90,
      render: (d) => <Tag color={getDifficultyColor(d)}>{difficultyName(d)}</Tag>
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (s) => <Tag color={getHabitStatusColor(s)}>{statusName(s)}</Tag>
    },
    {
      title: '操作', key: 'action', width: 130,
      render: (_, r) => (
        <Space>
          {!isGood && (
            <Tooltip title="记录进度">
              <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handleOpenProgress(r)} />
            </Tooltip>
          )}
          <Tooltip title="编辑">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => isGood ? handleOpenGoodHabit(r) : handleOpenHabit(r)} />
          </Tooltip>
          <Popconfirm
            title={isGood ? '确定要删除这个好习惯吗？' : '确定要删除这个习惯吗？'}
            onConfirm={() => isGood ? handleDeleteGoodHabit(r.id) : handleDeleteHabit(r.id)}
            okText="确定" cancelText="取消"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ]);

  // 习惯卡片（复用）
  const renderHabitCard = (habit, isGood) => {
    const impact = isGood ? habit.positive_impact : habit.negative_impact;
    return (
      <Col xs={24} sm={12} lg={8} key={habit.id}>
        <div className="mod-card">
          <div className="mod-card-head">
            <div className="mod-card-icon" style={{
              background: `${getHabitStatusColor(habit.status)}22`,
              color: getHabitStatusColor(habit.status)
            }}>
              {isGood ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mod-card-title">
                {habit.is_starred && <StarFilled style={{ color: '#FAAD14', marginRight: 6 }} />}
                {habit.name}
              </div>
              <div className="mod-card-sub">
                {isGood ? '积极影响' : '负面影响'}：{impact || '暂无描述'}
              </div>
            </div>
          </div>

          <div className="mod-card-body">
            <div className="mod-badges">
              <span className={`mod-badge ${habit.status === 'completed' ? 'mod-badge-primary' : habit.status === 'active' ? 'mod-badge-success' : habit.status === 'failed' ? 'mod-badge-danger' : 'mod-badge-warning'}`}>
                {statusName(habit.status)}
              </span>
              <span className={`mod-badge ${habit.difficulty === 'easy' ? 'mod-badge-success' : habit.difficulty === 'hard' ? 'mod-badge-danger' : 'mod-badge-warning'}`}>
                难度 · {difficultyName(habit.difficulty)}
              </span>
              {habit.start_date && (
                <span className="mod-badge">起始 {habit.start_date}</span>
              )}
            </div>
            {habit.notes && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#8c8c8c' }}>
                备注：{habit.notes}
              </div>
            )}
          </div>

          <div className="mod-card-actions">
            <Space size={0}>
              {!isGood && (
                <Tooltip title="记录进度">
                  <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handleOpenProgress(habit)} />
                </Tooltip>
              )}
              <Tooltip title="编辑">
                <Button type="text" size="small" icon={<EditOutlined />} onClick={() => isGood ? handleOpenGoodHabit(habit) : handleOpenHabit(habit)} />
              </Tooltip>
            </Space>
            <Popconfirm
              title={isGood ? '确定要删除这个好习惯吗？' : '确定要删除这个习惯吗？'}
              onConfirm={() => isGood ? handleDeleteGoodHabit(habit.id) : handleDeleteHabit(habit.id)}
              okText="确定" cancelText="取消"
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </div>
        </div>
      </Col>
    );
  };

  const renderList = (list, isGood) => {
    if (!list.length) {
      return (
        <Card style={{ borderRadius: 14 }}>
          <Empty description={isGood ? '暂无好习惯记录' : '暂无坏习惯记录'}>
            <Button type="primary" icon={<PlusOutlined />} style={{ background: BRAND, borderColor: BRAND }}
              onClick={() => isGood ? handleOpenGoodHabit() : handleOpenHabit()}>
              {isGood ? '记录好习惯' : '记录坏习惯'}
            </Button>
          </Empty>
        </Card>
      );
    }
    if (viewMode === 'table') {
      return (
        <Card style={{ borderRadius: 14 }} styles={{ body: { padding: 8 } }}>
          <Table
            rowKey="id"
            size="middle"
            columns={buildColumns(isGood)}
            dataSource={list}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            scroll={{ x: 640 }}
          />
        </Card>
      );
    }
    return <Row gutter={[16, 16]}>{list.map((h) => renderHabitCard(h, isGood))}</Row>;
  };

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            style={{ paddingLeft: 0, marginBottom: 4, color: '#8c8c8c' }}
          >
            返回
          </Button>
          <h1 className="mod-header-title">
            <SyncOutlined style={{ marginRight: 8, color: BRAND }} />
            {habitsConfig.name}
          </h1>
          <div className="mod-header-subtitle">{habitsConfig.description} · 打卡追踪 · 连续天数与完成率</div>
        </div>
        <Space wrap>
          <Button icon={<CalendarOutlined />} onClick={() => handleOpenCheckin(dayjs())}>
            今日打卡
          </Button>
          <Button icon={<InfoCircleOutlined />} onClick={() => setShowTipsModal(true)}>
            习惯TIPS
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => activeTab === 'good-habits' ? handleOpenGoodHabit() : handleOpenHabit()}
            style={{ background: BRAND, borderColor: BRAND }}
          >
            {activeTab === 'good-habits' ? '记录好习惯' : '记录坏习惯'}
          </Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        {statCards.map((c, i) => (
          <div
            key={i}
            className={`mod-stat-card${c.onClick ? ' clickable' : ''}${c.danger ? ' danger' : ''}`}
            onClick={c.onClick}
          >
            <span className="mod-stat-icon" style={{ color: c.color }}>{c.icon}</span>
            <div>
              <div className="mod-stat-value">{c.value}</div>
              <div className="mod-stat-label">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* 今日打卡进度条 */}
      {today.total ? (
        <Card style={{ borderRadius: 14, marginBottom: 16 }} styles={{ body: { padding: '14px 18px' } }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <Text strong>今日打卡完成度</Text>
            <Text type="secondary">{today.done || 0} / {today.total} 项 · 剩余 {today.pending || 0} 项</Text>
          </div>
          <Progress
            percent={today.completion || 0}
            strokeColor={{ from: BRAND, to: '#8B5CF6' }}
            style={{ marginTop: 6 }}
          />
        </Card>
      ) : null}

      {/* 工具栏 */}
      <Card className="mod-toolbar" styles={{ body: { padding: 12 } }}>
        <div className="mod-toolbar-row">
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            placeholder="搜索习惯名称 / 影响 / 备注"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 240, maxWidth: '100%' }}
          />
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 130 }}>
            <Option value="all">全部状态</Option>
            {habitsConfig.habitStatus.map(s => <Option key={s.key} value={s.key}>{s.name}</Option>)}
          </Select>
          <Select value={filterDifficulty} onChange={setFilterDifficulty} style={{ width: 130 }}>
            <Option value="all">全部难度</Option>
            {habitsConfig.difficultyLevels.map(d => <Option key={d.key} value={d.key}>{d.name}</Option>)}
          </Select>
          <Select value={sortKey} onChange={setSortKey} style={{ width: 130 }}>
            <Option value="recent">最近添加</Option>
            <Option value="name">名称排序</Option>
            <Option value="starred">收藏优先</Option>
          </Select>
          <Button
            type={onlyStarred ? 'primary' : 'default'}
            icon={onlyStarred ? <StarFilled /> : <StarOutlined />}
            onClick={() => setOnlyStarred(v => !v)}
            style={onlyStarred ? { background: '#FAAD14', borderColor: '#FAAD14' } : {}}
          >
            收藏
          </Button>
          <div style={{ marginLeft: 'auto' }}>
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'card', icon: <AppstoreOutlined /> },
                { value: 'table', icon: <UnorderedListOutlined /> }
              ]}
            />
          </div>
        </div>
      </Card>

      {/* 主要内容 */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'habits',
            label: <span><ExclamationCircleOutlined /> 坏习惯 ({filteredHabits.length})</span>,
            children: renderList(filteredHabits, false)
          },
          {
            key: 'good-habits',
            label: <span><CheckCircleOutlined /> 好习惯 ({filteredGoodHabits.length})</span>,
            children: renderList(filteredGoodHabits, true)
          },
          {
            key: 'calendar',
            label: <span><CalendarOutlined /> 打卡月历</span>,
            children: (
              <Card style={{ borderRadius: 14 }}>
                <Calendar
                  dateCellRender={dateCellRender}
                  onSelect={handleOpenCheckin}
                  style={{ borderRadius: 8 }}
                />
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <Space wrap size={16}>
                    {[
                      { c: '#52C41A', t: '优秀 (≥80%)' },
                      { c: '#1890FF', t: '良好 (60-79%)' },
                      { c: '#FAAD14', t: '一般 (40-59%)' },
                      { c: '#FF4D4F', t: '较差 (<40%)' }
                    ].map((it, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: it.c, marginRight: 8 }} />
                        <Text type="secondary">{it.t}</Text>
                      </span>
                    ))}
                  </Space>
                </div>
              </Card>
            )
          }
        ]}
      />

      {/* 坏习惯弹窗 */}
      <Modal
        open={showHabitModal}
        title={selectedHabit ? '编辑坏习惯' : '记录坏习惯'}
        onCancel={() => setShowHabitModal(false)}
        onOk={() => habitForm.submit()}
        okText={selectedHabit ? '更新习惯' : '记录习惯'}
        cancelText="取消"
        okButtonProps={{ style: { background: BRAND, borderColor: BRAND } }}
        destroyOnClose
        width={640}
      >
        <Form form={habitForm} layout="vertical" onFinish={handleSaveHabit} preserve={false}>
          <Form.Item name="name" label="习惯名称" rules={[{ required: true, message: '请输入习惯名称' }]}>
            <Input placeholder="请输入习惯名称" />
          </Form.Item>
          <Form.Item name="negative_impact" label="负面影响" rules={[{ required: true, message: '请输入负面影响' }]}>
            <TextArea rows={3} placeholder="请描述这个习惯的负面影响" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="difficulty" label="纠正难度" rules={[{ required: true, message: '请选择纠正难度' }]}>
                <Select placeholder="请选择难度">
                  {habitsConfig.difficultyLevels.map(d => <Option key={d.key} value={d.key}>{d.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status" label="当前状态" rules={[{ required: true, message: '请选择当前状态' }]}>
                <Select placeholder="请选择状态">
                  {habitsConfig.habitStatus.map(s => <Option key={s.key} value={s.key}>{s.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="start_date" label="开始纠正日期">
                <DatePicker style={{ width: '100%' }} placeholder="选择日期" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="is_starred" label="标记" valuePropName="checked">
                <Checkbox>标为重点关注</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="备注">
            <TextArea rows={2} placeholder="请添加备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 好习惯弹窗 */}
      <Modal
        open={showGoodHabitModal}
        title={selectedGoodHabit ? '编辑好习惯' : '记录好习惯'}
        onCancel={() => setShowGoodHabitModal(false)}
        onOk={() => goodHabitForm.submit()}
        okText={selectedGoodHabit ? '更新好习惯' : '记录好习惯'}
        cancelText="取消"
        okButtonProps={{ style: { background: BRAND, borderColor: BRAND } }}
        destroyOnClose
        width={640}
      >
        <Form form={goodHabitForm} layout="vertical" onFinish={handleSaveGoodHabit} preserve={false}>
          <Form.Item name="name" label="习惯名称" rules={[{ required: true, message: '请输入习惯名称' }]}>
            <Input placeholder="请输入好习惯名称" />
          </Form.Item>
          <Form.Item name="positive_impact" label="积极影响" rules={[{ required: true, message: '请描述积极影响' }]}>
            <TextArea rows={3} placeholder="请描述这个好习惯的积极影响" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="difficulty" label="养成难度" rules={[{ required: true, message: '请选择养成难度' }]}>
                <Select placeholder="请选择养成难度">
                  {habitsConfig.difficultyLevels.map(level => (
                    <Option key={level.key} value={level.key}>{level.name} - {level.description}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
                <Select placeholder="请选择状态">
                  {habitsConfig.habitStatus.map(status => (
                    <Option key={status.key} value={status.key}>{status.name} - {status.description}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="start_date" label="开始养成日期">
                <DatePicker style={{ width: '100%' }} placeholder="选择日期" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="is_starred" label="标记" valuePropName="checked">
                <Checkbox>标为重点关注</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="备注">
            <TextArea rows={2} placeholder="请添加备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 进度弹窗 */}
      <Modal
        open={showProgressModal}
        title={`记录进度 - ${selectedHabit?.name || ''}`}
        onCancel={() => setShowProgressModal(false)}
        onOk={() => progressForm.submit()}
        okText="保存进度"
        cancelText="取消"
        okButtonProps={{ style: { background: BRAND, borderColor: BRAND } }}
        destroyOnClose
        width={600}
      >
        <Form form={progressForm} layout="vertical" onFinish={handleSaveProgress} preserve={false}>
          <Form.Item name="content" label="进度内容" rules={[{ required: true, message: '请输入进度内容' }]}>
            <TextArea rows={4} placeholder="请详细描述您的纠正进度" />
          </Form.Item>
          <Form.Item name="progress" label="纠正进度" rules={[{ required: true, message: '请输入纠正进度' }]}>
            <Input type="number" min={0} max={100} placeholder="请输入0-100的数字" />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <TextArea rows={2} placeholder="请添加备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 习惯打卡弹窗 */}
      <Modal
        open={showCheckinModal}
        title={`习惯打卡 - ${selectedDate.format('YYYY年MM月DD日')}`}
        onCancel={() => setShowCheckinModal(false)}
        onOk={() => checkinForm.submit()}
        okText="保存打卡"
        cancelText="取消"
        okButtonProps={{ style: { background: BRAND, borderColor: BRAND } }}
        destroyOnClose
        width={600}
      >
        <Form form={checkinForm} layout="vertical" onFinish={handleSaveCheckin} preserve={false}>
          {habitsConfig.checkinOptions.map((option) => (
            <Form.Item key={option.key} name={option.key} valuePropName="checked" style={{ marginBottom: 12 }}>
              <Checkbox>
                <div style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 10,
                  padding: 12,
                  backgroundColor: '#fafafa',
                  marginTop: 4
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 20, marginRight: 8 }}>{option.icon}</span>
                    <Text strong>{option.question}</Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>{option.description}</Text>
                </div>
              </Checkbox>
            </Form.Item>
          ))}
          <Form.Item name="mood" label="今日心情">
            <Select allowClear placeholder="选择心情（可选）">
              <Option value="great">😄 很棒</Option>
              <Option value="good">🙂 不错</Option>
              <Option value="normal">😐 一般</Option>
              <Option value="bad">😔 低落</Option>
            </Select>
          </Form.Item>
          <Form.Item name="note" label="打卡备注">
            <TextArea rows={2} placeholder="记录今日心得（可选）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 习惯TIPS弹窗 */}
      <Modal
        open={showTipsModal}
        title={<span><BulbOutlined style={{ color: BRAND, marginRight: 8 }} />习惯管理TIPS</span>}
        onCancel={() => setShowTipsModal(false)}
        footer={<Button type="primary" style={{ background: BRAND, borderColor: BRAND }} onClick={() => setShowTipsModal(false)}>我知道了</Button>}
        width={820}
      >
        <div style={{ padding: '8px 0' }}>
          <Card title="习惯养成周期" style={{ marginBottom: 16, borderRadius: 12 }}>
            <Row gutter={[16, 16]}>
              {habitsConfig.habitFormationPeriods.periods.map((period, index) => (
                <Col xs={24} md={12} key={index}>
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%', backgroundColor: period.color,
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 'bold', marginRight: 12
                      }}>
                        {period.period}
                      </div>
                      <Title level={5} style={{ margin: 0 }}>{period.description}</Title>
                    </div>
                    <Text type="secondary">示例: {period.examples}</Text>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          <Card title="习惯管理TIPS" style={{ marginBottom: 16, borderRadius: 12 }}>
            <Timeline
              items={habitsConfig.habitManagementTips.tips.map((tip) => ({
                children: (
                  <>
                    <Title level={5} style={{ marginBottom: 4 }}>{tip.title}</Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{tip.description}</Text>
                    <div style={{ backgroundColor: '#f6f6f6', padding: 8, borderRadius: 4, fontSize: 12 }}>
                      示例: {tip.example}
                    </div>
                  </>
                )
              }))}
            />
          </Card>

          <Card title="习惯养成四大法则" style={{ borderRadius: 12 }}>
            <Steps direction="vertical" size="small">
              {habitsConfig.habitFormationMethods.methods.map((method, index) => (
                <Step
                  key={index}
                  title={method.title}
                  description={method.description}
                  icon={<span style={{ fontSize: 16 }}>{method.icon}</span>}
                />
              ))}
            </Steps>
          </Card>
        </div>
      </Modal>
    </div>
  );
};

export default Habits;
